import { isUndefined, uniq } from 'lodash-es';
import { isNotNullable } from '@bayer-int/portletce-shared';
import * as t from 'io-ts';

import type {
    ConditionInputDefinition,
    InteractiveTitleSelectDefinition,
    InteractiveTitleSettingDefinition,
    InteractiveTitleNumberInputDefinition,
} from 'header/types';
import type { BWQuery } from 'portlet/models/query';
import { isBWQuery } from 'portlet/models/query';

import { define, createSpecSettingsInterface } from './_common';

export interface ConditionSettingDefinition extends ConditionInputDefinition {
    key: 'CONDITION';
}

export const CONDITION_MAX_VALUE = 50;

const definition: ConditionSettingDefinition = {
    key: 'CONDITION',
    type: 'Condition',
    allowConditionChange: true,
    conditionMaxValue: CONDITION_MAX_VALUE,
    conditionVariableName: '',
    label: 'condition',
    value: { key: '', value: 1 },
    options: [],
};

const specSettingsInterface = createSpecSettingsInterface(
    definition,
    t.type({ key: t.string, value: t.number }),
);

export const codec = specSettingsInterface.input;

export const ConditionSettingDefinition = define({
    key: 'CONDITION',
    label: 'condition',
    definition,
    fromSetting: settings => specSettingsInterface.extractValue(settings.condition),
    createDefinitionWithOpts: (opts = {}) => ({ ...definition, ...opts }),
    createDefinitionFromSpec: (settings, spec, view) => {
        if (isUndefined(settings.condition)) return;

        const query = view?.query || spec.query;
        if (!query || !isBWQuery(query)) return;

        return {
            ...definition,
            ...specSettingsInterface.createDefinitionWithSettings(settings.condition),
            allowConditionChange: query.querySettings.allowConditionChange,
            conditionMaxValue: query.querySettings.conditionMaxValue,
            conditionVariableName: query.querySettings.conditionVariableName,
            options: query.conditions,
        };
    },

    applySettingDependency: ({ diff, specification, view, settings }) => {
        const query = view?.query || specification.query;
        const viewChanged = !!diff.VIEWID && !!diff.VIEWID.previous;
        const currentView = settings.VIEWID;
        const currentCondition = settings.CONDITION;

        if (!query || !viewChanged || !isBWQuery(query)) return;

        const condition = findConditionByView(query, currentView, currentCondition);

        return condition
            ? {
                  value: {
                      key: condition.key,
                      value: settings.CONDITION?.value ?? CONDITION_MAX_VALUE,
                      group: condition.group,
                  },
              }
            : // if current condition is not applicable to new view/query
              // we unset the setting
              { value: undefined };
    },

    replacementTags: definition => ({
        CONDITION_VALUE: definition.value?.value.toString() || '',
        CONDITION_LABEL:
            definition.options.find(opt => opt.key === definition.value?.key)?.label || '',
        CONDITION_GROUP:
            definition.options.find(opt => opt.key === definition.value?.key)?.group || '',
    }),

    interactiveTags: definition => {
        const valueTag: InteractiveTitleSettingDefinition<
            ConditionSettingDefinition,
            InteractiveTitleNumberInputDefinition
        > = {
            setting: definition,
            field: {
                type: 'Number',
                value: definition.value.value,
                min: 1,
                max: 50,
                step: 1,
            },
            format: value => ({ ...definition, value: { ...definition.value, value } }),
        };

        const labelTag: InteractiveTitleSettingDefinition<
            ConditionSettingDefinition,
            InteractiveTitleSelectDefinition
        > = {
            setting: definition,
            field: {
                type: 'Select',
                value: definition.value.key,
                options:
                    definition.value && definition.value.group
                        ? definition.options.filter(
                              ({ group }) => definition.value && group === definition.value.group,
                          )
                        : definition.options,
            },
            format: keyValue => ({
                ...definition,
                value: {
                    ...definition.value,
                    ...definition.options.find(({ key }) => key === keyValue),
                },
            }),
        };

        const groupTag: InteractiveTitleSettingDefinition<
            ConditionSettingDefinition,
            InteractiveTitleSelectDefinition
        > = {
            setting: definition,
            field: {
                type: 'Select',
                value: definition.value.group || '',
                options: uniq(definition.options.map(({ group }) => group))
                    .filter(isNotNullable)
                    .map(key => ({ key, label: key })),
            },
            format: groupValue => ({
                ...definition,
                value: {
                    ...definition.value,
                    group: groupValue,
                    // if current value does not belong to select group, find the first one the does
                    ...(definition.value.group !== groupValue &&
                        definition.options.find(({ group }) => group === groupValue)),
                },
            }),
        };

        return {
            CONDITION_VALUE: valueTag,
            CONDITION_LABEL: labelTag,
            CONDITION_GROUP: groupTag,
        };
    },
});

function findConditionByView(
    query: BWQuery,
    viewId: number | undefined,
    condition: ConditionInputDefinition['value'] | undefined,
): BWQuery['conditions'][number] | undefined {
    // find the current condition definition by `key`, because value stored in settings
    // may lack the group attribute (e.g. if not provided by user in config)
    // it could also be the case that after view change the query (and therefore list of conditions) changes
    const currentCondition = condition && query.conditions.find(({ key }) => key === condition.key);

    // if there is current condition with group, try to select condition within the same group with matching view id
    const matchingByViewAndGroup =
        currentCondition &&
        currentCondition.group &&
        query.conditions.find(
            ({ defaultView, group }) => defaultView === viewId && group === currentCondition.group,
        );

    // otherwise just take the first condition which matches by view id
    const matchingByView = query.conditions.find(({ defaultView }) => defaultView === viewId);

    return (
        matchingByViewAndGroup ||
        matchingByView ||
        //  if no condition with `defaultView` is found for new view, we preserve the current condition
        // (note the distinction between condition and currentCondition)
        currentCondition
    );
}
